import axios from 'axios';
import { toast } from 'sonner';
import { reportLatency, isLatencySample } from './networkQuality';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: false,
  // Generous timeout — hunters in the forest have very weak signal, the request
  // can take 20+s but still succeed. Better that than failing every call.
  timeout: 25000,
});

// Hybrid auth: send Bearer token (cross-origin) alongside cookies (same-origin)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('obgh_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global response interceptor — surfaces clear messages on common failure modes
// (network down, 401 expired session, 429 rate limit). Each call site can still
// catch and override, but at least there's always a visible toast.
let lastToastAt = 0;
const throttleToast = (fn) => {
  // 30s window — hunters with weak forest signal should NOT see spam.
  const now = Date.now();
  if (now - lastToastAt > 30000) {
    lastToastAt = now;
    fn();
  }
};

// Broadcast a global "offline detected" event so any banner component can react
// without each call site needing to know. Listen via window.addEventListener('obgh:offline').
const emitOffline = () => {
  try { window.dispatchEvent(new CustomEvent('obgh:offline', { detail: { at: Date.now() } })); } catch { /* SSR safe */ }
};

api.interceptors.request.use((config) => { config.metadata = { startedAt: Date.now() }; return config; });

api.interceptors.response.use(
  (resp) => { if (isLatencySample(resp.config?.url)) reportLatency(Date.now() - (resp.config?.metadata?.startedAt || Date.now())); return resp; },
  (error) => {
    // Suppress noise on cancelled/aborted requests.
    if (axios.isCancel?.(error) || error.code === 'ERR_CANCELED') return Promise.reject(error);
    if (!error.response && isLatencySample(error.config?.url)) reportLatency(0, true);

    // No response = network failure or CORS or timeout.
    if (!error.response) {
      const isTimeout = error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '');
      if (isTimeout) {
        throttleToast(() => toast.error('L\'opération prend plus de temps que prévu — réessayez dans un instant'));
        return Promise.reject(error);
      }
      emitOffline();
      // Don't spam — the persistent OfflineBanner is the primary surface.
      // Only show a one-shot toast every 30s so user knows what happened.
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
      throttleToast(() => toast.error(
        offline
          ? 'Mode hors-ligne — utilise les données déjà chargées'
          : 'Signal trop faible — réessayez ou activez le Mode hors-ligne'
      ));
      return Promise.reject(error);
    }

    const { status } = error.response;
    const url = error.config?.url || '';

    // 401 on a protected endpoint typically means JWT expired.
    // On exclut /auth/me, /auth/login, /auth/register car un 401 sur ces
    // endpoints est attendu quand l'utilisateur n'est pas (encore) connecté.
    const isAuthProbe = url.includes('/auth/me') || url.includes('/auth/login') || url.includes('/auth/register');
    if (status === 401 && !isAuthProbe) {
      throttleToast(() => toast.error('Session expirée — reconnectez-vous'));
    } else if (status === 429) {
      throttleToast(() => toast.error('Trop de requêtes — patientez quelques secondes'));
    } else if (status >= 500) {
      throttleToast(() => toast.error('Erreur serveur — réessayez dans un instant'));
    }
    return Promise.reject(error);
  }
);

// Spots API (also exported as sitesApi for terminology consistency)
export const spotsApi = {
  getSpots: async () => {
    const response = await api.get('/spots');
    return response.data;
  },
  getSites: async () => {
    const response = await api.get('/spots');
    return response.data;
  },
  getAll: (userId) => api.get(`/spots?user_id=${userId}`),
  getOne: (spotId, userId) => api.get(`/spots/${spotId}?user_id=${userId}`),
  create: async (spotData) => {
    const response = await api.post('/spots', spotData);
    return response.data;
  },
  update: (spotId, userId, spotData) => api.put(`/spots/${spotId}?user_id=${userId}`, spotData),
  delete: async (spotId) => {
    const response = await api.delete(`/spots/${spotId}`);
    return response.data;
  },
};

// Alias for sitesApi (terminology: SPOTS → SITES)
export const sitesApi = spotsApi;

export default api;
